import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../constants';

export const SetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordVisible, setShowPasswordVisible] = useState(false);
  const [showConfirmPasswordVisible, setShowConfirmPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // (LOGIK OFÖRÄNDRAD)
  const [loading, setLoading] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [invites, setInvites] = useState([]); // [{companyId, companyName, role}]
  const [showTooltip, setShowTooltip] = useState(false);

  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        iconRef.current &&
        !iconRef.current.contains(event.target)
      ) {
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setError('');
        setLoading(true);
        const { data } = await axios.get(`${BACKEND_URL}/set-password/inspect`, {
          params: { token },
          withCredentials: true,
        });
        if (!mounted) return;
        if (data.status === 'ok') {
          setHasPassword(!!data.hasPassword);
          setEmail(data.email);
          setInvites(Array.isArray(data.invites) ? data.invites : []);
        } else {
          setError(data.errorText || 'Länken är ogiltig eller har gått ut.');
        }
      } catch (err) {
        setError('Ett fel uppstod. Försök igen.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  // Samma validering som din nuvarande (för att INTE ändra logik)
  const validatePassword = (pwd) => {
    const minLength = /.{8,}/;
    const hasUppercase = /[A-Z]/; // behåller exakt detta (ej ÅÄÖ) för att matcha din logik
    const hasNumber = /[0-9]/;
    return (
      minLength.test(pwd) &&
      hasUppercase.test(pwd) &&
      hasNumber.test(pwd) 
    );
  };

  // UI-checklista (visuellt stöd, påverkar inte logiken)
  const checks = useMemo(() => ({
    minLen: /.{8,}/.test(password),
    upper: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    match: confirmPassword.length > 0 && confirmPassword === password,
  }), [password, confirmPassword]);

  // Accept-only flow (OFÖRÄNDRAD LOGIK)
  const handleAccept = async () => {
    try {
      setError('');
      const { data } = await axios.post(`${BACKEND_URL}/set-password`, { token }, { withCredentials: true });
      if (data.status === 'accepted' || data.status === 'success') {
        setSuccess(true);
      } else {
        setError(data.errorText || 'Ett fel uppstod. Försök igen.');
      }
    } catch {
      setError('Ett fel uppstod. Försök igen.');
    }
  };

  // Set password flow (OFÖRÄNDRAD LOGIK)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) return setError('Lösenorden matchar inte');
    if (!validatePassword(password)) {
      return setError(
        'Lösenordet måste vara minst 8 tecken långt och innehålla minst en stor bokstav och ett nummer.'
      );
    }
    try {
      const { data } = await axios.post(`${BACKEND_URL}/set-password`, { token, password }, { withCredentials: true });
      if (data.status === 'success') {
        setSuccess(true);
      } else {
        setError(data.errorText || 'Ett fel uppstod. Försök igen.');
      }
    } catch {
      setError('Ett fel uppstod. Försök igen.');
    }
  };

  const EyeIcon = ({ visible }) =>
    visible ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5.05 0-9.27-3.14-11-7.5a11.09 11.09 0 0 1 4.27-5.09M1 1l22 22" />
        <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 2.47-6M12 12" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );

  const Rule = ({ ok, text }) => (
    <div style={{ ...styles.ruleItem, opacity: ok ? 1 : 0.9 }}>
      <span style={{ ...styles.ruleDot, background: ok ? '#29d17f' : '#cccccc' }} />
      <span style={{ color: '#fff' }}>{text}</span>
    </div>
  );

  // ——— LAYOUT ———
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Laddar…</h1>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Klart! 🎉</h1>
          <p style={styles.subtleText}>
            {hasPassword
              ? <>Inbjudan accepterad. Öppna <strong>TimeSmart</strong> och logga in i appen.</>
              : <>Ditt lösenord är sparat. Ladda ner <strong>TimeSmart</strong> och logga in i appen.</>}
            <br /><span style={{ opacity: 0.9 }}>(Inloggning via webben är endast för administratörer.)</span>
          </p>

          <div style={styles.storeButtons}>
            <a href="https://apps.apple.com/us/app/timesmart/id6743060091" target="_blank" rel="noopener noreferrer" style={{ ...styles.storeBtn, ...styles.appleBtn }} aria-label="Hämta på App Store">
              <span style={styles.storeIcon} aria-hidden></span>
              <span>
                <div style={styles.storeSmallLabel}>Hämta på</div>
                <div style={styles.storeBigLabel}>App Store</div>
              </span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.hugge69.timesmart.ios" target="_blank" rel="noopener noreferrer" style={{ ...styles.storeBtn, ...styles.googleBtn }} aria-label="Hämta på Google Play">
              <span style={styles.storeIcon} aria-hidden>▶</span>
              <span>
                <div style={styles.storeSmallLabel}>Hämta på</div>
                <div style={styles.storeBigLabel}>Google Play</div>
              </span>
            </a>
          </div>

          <p style={styles.noteText}>Har du redan appen installerad? Öppna den och logga in.</p>
        </div>
      </div>
    );
  }

  if (hasPassword) {
    // Accept-only UI — design matchad, logik oförändrad
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Acceptera inbjudan</h1>
          <p style={styles.subtleText}>
            {email ? <>Inbjudan är skickad till <strong>{email}</strong>.</> : null}
            {invites?.length ? (
              <>
                <br />Du blir medlem i:
                <ul style={styles.inviteList}>
                  {invites.map((i, idx) => (
                    <li key={idx}>{i.companyName} — {i.role}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </p>

          <button onClick={handleAccept} style={styles.primaryBtn}>Acceptera inbjudan</button>
          {error && <p style={styles.error}>{error}</p>}
        </div>
      </div>
    );
  }

  // Set password UI — design matchad, logik oförändrad
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Välkommen till TimeSmart</h1>
        <p style={styles.subtleText}>Skapa ditt lösenord nedan.</p>

        <button
          type="button"
          ref={iconRef}
          style={styles.infoIconBtn}
          onClick={() => setShowTooltip(!showTooltip)}
          title="Krav för lösenord"
          aria-expanded={showTooltip}
        >
          ℹ️
        </button>
        {showTooltip && (
          <div ref={tooltipRef} style={styles.tooltip} role="note">
            <p>• Minst 8 tecken</p>
            <p>• Minst ett nummer</p>
            <p>• Minst en stor bokstav</p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
          <div style={styles.inputWrap}>
            <input
              type={showPasswordVisible ? 'text' : 'password'}
              placeholder="Lösenord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
              autoComplete="new-password"
              aria-label="Lösenord"
            />
            <button
              type="button"
              onClick={() => setShowPasswordVisible(v => !v)}
              style={styles.eyeBtn}
              title={showPasswordVisible ? 'Dölj lösenord' : 'Visa lösenord'}
              aria-label={showPasswordVisible ? 'Dölj lösenord' : 'Visa lösenord'}
            >
              <EyeIcon visible={showPasswordVisible} />
            </button>
          </div>

          <div style={styles.inputWrap}>
            <input
              type={showConfirmPasswordVisible ? 'text' : 'password'}
              placeholder="Bekräfta lösenord"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={styles.input}
              autoComplete="new-password"
              aria-label="Bekräfta lösenord"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPasswordVisible(v => !v)}
              style={styles.eyeBtn}
              title={showConfirmPasswordVisible ? 'Dölj lösenord' : 'Visa lösenord'}
              aria-label={showConfirmPasswordVisible ? 'Dölj lösenord' : 'Visa lösenord'}
            >
              <EyeIcon visible={showConfirmPasswordVisible} />
            </button>
          </div>

          {/* Endast visuell checklista – påverkar inte submit-logiken */}
          <div style={styles.rulesBox}>
            <Rule ok={checks.minLen}  text="Minst 8 tecken" />
            <Rule ok={checks.number}  text="Minst ett nummer" />
            <Rule ok={checks.upper}   text="Minst en stor bokstav" />
            <Rule ok={checks.match}   text="Lösenorden matchar" />
          </div>

          <button type="submit" style={styles.primaryBtn}>Skapa Lösenord</button>
        </form>

        {error && <p style={styles.error}>{error}</p>}
      </div>
    </div>
  );
};

const styles = {
  // Samma bakgrund & glas-kort som referensen
  page: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: 'linear-gradient(135deg, #5DADE2, #1f4e8c)',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    padding: 30,
    borderRadius: 18,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.18)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    color: '#fff',
    position: 'relative',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: '1.8rem',
    fontWeight: 800,
    lineHeight: 1.2,
    color: '#fff',
  },
  subtleText: {
    marginTop: 10,
    marginBottom: 18,
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.95)',
  },

  // Inputs + eye-button
  inputWrap: {
    position: 'relative',
    marginBottom: 14,
  },
  input: {
    width: '100%',
    height: 48,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.35)',
    background: 'rgba(255,255,255,0.95)',
    fontSize: '1rem',
    padding: '0 44px 0 14px',
    outline: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  eyeBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    height: 36,
    width: 38,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 10,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#333',
  },

  // Tooltip (samma look)
  infoIconBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    fontSize: '1.3rem',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
  },
  tooltip: {
    position: 'absolute',
    top: 44,
    right: 12,
    width: 220,
    backgroundColor: '#fff',
    color: '#333',
    padding: 12,
    borderRadius: 10,
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
    textAlign: 'left',
    fontSize: '0.95rem',
    zIndex: 100,
  },

  // Regler/Checklist
  rulesBox: {
    marginTop: 14,
    marginBottom: 6,
    display: 'grid',
    gap: 6,
  },
  ruleItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  ruleDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
  },

  // CTA (matchar referensen)
  primaryBtn: {
    width: '100%',
    height: 48,
    marginTop: 8,
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0b2447',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
    boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
    transition: 'transform 0.06s ease, box-shadow 0.2s ease',
  },

  // Felmeddelande
  error: {
    marginTop: 12,
    color: '#ffe1e6',
    background: 'rgba(220,53,69,0.20)',
    border: '1px solid rgba(220,53,69,0.45)',
    borderRadius: 10,
    padding: '8px 10px',
    fontWeight: 500,
  },

  // Invites
  inviteList: {
    margin: '8px 0 0',
    paddingLeft: 18,
    textAlign: 'left',
    color: '#fff',
  },

  // Store buttons (samma look)
  storeButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 10,
    marginTop: 6,
    marginBottom: 8,
  },
  storeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '12px 16px',
    borderRadius: 14,
    textDecoration: 'none',
    color: 'white',
    justifyContent: 'center',
    fontWeight: 700,
    boxShadow: '0 8px 18px rgba(0,0,0,0.25)',
  },
  appleBtn: { background: '#000000' },
  googleBtn: { background: '#1a73e8' },
  storeIcon: { fontSize: '1.4rem', lineHeight: 1 },
  storeSmallLabel: { fontSize: '0.8rem', opacity: 0.9, marginBottom: 2 },
  storeBigLabel: { fontSize: '1.2rem', lineHeight: 1.1 },

  noteText: { marginTop: 8, color: 'white', fontSize: '0.95rem', opacity: 0.95 },
};

export default SetPassword;
